/**
 * MODULE: domain/sales-presentations/frontend/api/client
 * PURPOSE: API client for presentations, templates, adaptive templates, generation.
 * DEPENDENCIES: axios, _shared/lib/frontend/api/config
 * PUBLIC API: presentationsApi, templatesApi, adaptiveTemplatesApi, themesApi, analyticsApi, etc.
 * SIDE_EFFECTS: HTTP requests; redirect to /login on 401
 * AGENT_SAFETY: 🟢 SAFE
 * Recent: 2026-05-12 [2.2.957] – **`generateWithStream`** SSE outline progress uses **`OUTLINE_PROGRESS_NODES`** and **`outlineProgressIdx`** (aligned naming; **`CHANGELOG` [2.2.957]**).
 * Recent: 2026-02-26 [2.2.146] – presentationsApi.generateWithStream (POST /generate/stream SSE).
 * Recent: 2026-05-12 – normalizePresentationFromApi on list/get/generate/update/stream so slide.layers defaults to [].
 * Recent: 2026-05-12 – E5-4 **`getDeckEngagement`**, **`SlideEngagement`**, **`DeckEngagementData`** (per-deck engagement API).
 * Recent: 2026-05-12 – E5-7 **`crmApi`**, engagement GET path **`/analytics/decks/:id/engagement`**, types from **`./types`** (no inline analytics or CRM deal shapes).
 * Recent: 2026-05-13 – E4-3 presentationsApi.getShareStatus, ensureShare, revokeShare, getByShareToken (public GET, no auth).
 * Recent: 2026-05-12 – presentationsApi.exportPdf (POST /{id}/export/pdf, blob) for E4-2.
 * Recent: 2026-05-13 – presentationsApi.exportPptx (POST /{id}/export/pptx, blob) for E4-6.
 */

import axios, { AxiosInstance } from 'axios';
import { API_CONFIG, getAuthHeaders } from '../../../../_shared/lib/frontend/api/config';
import type {
  Presentation,
  PresentationTemplate,
  GeneratePresentationRequest,
  GenerationConfig,
  OutlineSlide,
  GenerateOutlineResponse,
  SlideViewEvent,
  DeckCompletionEvent,
  SlideEngagement,
  DeckEngagementData,
  CRMDeal,
  PreviousDeckSummary,
  ContentSchema,
} from './types';
import { normalizePresentationFromApi } from '../utils/normalizeContentSchema';

type PresentationTemplateLayout = PresentationTemplate['layout_schema'];

const baseURL = API_CONFIG.baseURL
  ? `${API_CONFIG.baseURL}/api/v1/presentations`
  : '/api/v1/presentations';

const client: AxiosInstance = axios.create({
  baseURL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
  Object.assign(config.headers, getAuthHeaders());
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      const data = error.response?.data as { detail?: unknown } | undefined;
      const d = data?.detail;
      const code =
        d && typeof d === 'object' && d !== null && 'code' in d
          ? String((d as { code?: string }).code ?? '')
          : '';
      if (code === 'CHANNEL_AUTH_EXPIRED') {
        return Promise.reject(error);
      }
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/** E5-2 — CRM deal row from GET /api/v1/integrations/crm/deals (camel or snake_case). */
export interface IntegrationsCrmDealSummary {
  id: string;
  name: string;
  stage?: string;
  companyName?: string;
  company_name?: string;
  contactName?: string;
  contact_name?: string;
  contactTitle?: string;
  contact_title?: string;
  dealValue?: number;
  deal_value?: number;
  companyIndustry?: string;
  company_industry?: string;
  contactId?: string;
  contact_id?: string;
}

function coerceDealSearchResponse(raw: unknown): IntegrationsCrmDealSummary[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as IntegrationsCrmDealSummary[];
  if (typeof raw === 'object' && raw !== null && 'deals' in raw) {
    const d = (raw as { deals: unknown }).deals;
    return Array.isArray(d) ? (d as IntegrationsCrmDealSummary[]) : [];
  }
  return [];
}

/**
 * GET /api/v1/integrations/crm/deals?q= — returns [] when query &lt; 3 chars or on HTTP error
 * (graceful when route or CRM integration is unavailable).
 */
export async function searchIntegrationsCrmDeals(query: string): Promise<IntegrationsCrmDealSummary[]> {
  const q = query.trim();
  if (q.length < 3) return [];
  const base = API_CONFIG.baseURL ?? '';
  const url = `${base}/api/v1/integrations/crm/deals`;
  try {
    const { data } = await axios.get<unknown>(url, {
      params: { q },
      headers: getAuthHeaders(),
      timeout: 15000,
    });
    return coerceDealSearchResponse(data);
  } catch {
    return [];
  }
}

export interface PublishChannel {
  channelId: string;
  name: string;
  iconKey: string;
  lastPostedAt: string | null;
  needsReconnect: boolean;
}

export interface PublishPresentationResponse {
  channelName: string;
  postedAt: string;
  postUrl?: string | null;
}

/** E5-6 — GET /contacts/:contactId/deck-history (camelCase rows). */
export interface ContactDeckHistoryRow {
  deckId: string;
  title: string;
  sentAt: string;
  slideCount: number;
  keyTopics: string[];
}

export interface ContactDeckHistoryResponse {
  decks: ContactDeckHistoryRow[];
}

export const presentationsApi = {
  list: async (params?: {
    filters?: { account_id?: string; status?: string; created_by?: string };
    limit?: number;
    offset?: number;
  }): Promise<Presentation[]> => {
    const { data } = await client.get<Presentation[]>('', {
      params: {
        account_id: params?.filters?.account_id,
        status: params?.filters?.status,
        limit: params?.limit ?? 50,
        offset: params?.offset ?? 0,
      },
    });
    return Array.isArray(data) ? data.map(normalizePresentationFromApi) : [];
  },

  get: async (presentationId: string): Promise<Presentation> => {
    const { data } = await client.get<Presentation>(`/${presentationId}`);
    return normalizePresentationFromApi(data);
  },

  /** E5-6 — prior decks sent to a CRM contact (keyTopics read-only from server). */
  getContactDeckHistory: async (contactId: string): Promise<ContactDeckHistoryResponse> => {
    const { data } = await client.get<ContactDeckHistoryResponse>(
      `/contacts/${encodeURIComponent(contactId.trim())}/deck-history`
    );
    const raw = data as { decks?: unknown } | undefined;
    const decks = Array.isArray(raw?.decks) ? (raw.decks as ContactDeckHistoryRow[]) : [];
    return { decks };
  },

  generate: async (request: GeneratePresentationRequest): Promise<Presentation> => {
    const { data } = await client.post<Presentation>('', request);
    return normalizePresentationFromApi(data);
  },

  /** Generate with SSE streaming; calls onProgress; fetches full presentation on complete. */
  generateWithStream: async (
    request: GeneratePresentationRequest,
    onProgress: (event: { type: string; node?: string; status?: string; progress?: number }) => void
  ): Promise<Presentation> => {
    const base = API_CONFIG.baseURL ?? '';
    const url = `${base}/api/v1/presentations/generate/stream`;
    const headers = getAuthHeaders() as Record<string, string>;
    const res = await fetch(url, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        method: request.method,
        prompt: request.prompt,
        template_id: request.template_id,
        account_id: request.account_id,
        opportunity_id: request.opportunity_id,
        custom_data: request.custom_data,
        ...(request.crmContext && Object.keys(request.crmContext).length > 0
          ? { crmContext: request.crmContext }
          : {}),
      }),
    });
    if (!res.ok) {
      const err = await res.text();
      throw new Error(err || `Generation failed: ${res.status}`);
    }
    const reader = res.body?.getReader();
    if (!reader) throw new Error('Stream not available');
    const dec = new TextDecoder();
    let buf = '';
    let presentationId: string | null = null;
    let title: string | null = null;
    const OUTLINE_PROGRESS_NODES = ['analyze_prompt', 'recommend_template', 'generate_slide_content', 'finalize'];
    let outlineProgressIdx = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const lines = buf.split('\n\n');
      buf = lines.pop() ?? '';
      for (const chunk of lines) {
        if (!chunk.trim()) continue;
        const eventMatch = chunk.match(/^event:\s*(.+)/m);
        const dataMatch = chunk.match(/^data:\s*(.+)/m);
        const event = eventMatch?.[1]?.trim() ?? 'message';
        const dataRaw = dataMatch?.[1]?.trim();
        if (!dataRaw) continue;
        try {
          const data = JSON.parse(dataRaw);
          if (event === 'progress') {
            onProgress({ type: 'progress', node: data.node, status: data.status, progress: outlineProgressIdx * 25 });
            if (data.node && OUTLINE_PROGRESS_NODES.includes(data.node))
              outlineProgressIdx = Math.min(outlineProgressIdx + 1, OUTLINE_PROGRESS_NODES.length);
          } else if (event === 'complete') {
            presentationId = data.presentation_id;
            title = data.title;
            onProgress({ type: 'complete', progress: 100 });
          } else if (event === 'error') {
            throw new Error(data.error ?? 'Generation failed');
          }
        } catch (e) {
          if (e instanceof SyntaxError) continue;
          throw e;
        }
      }
    }
    if (!presentationId) throw new Error('Generation did not complete');
    const full = await client.get<Presentation>(`/${presentationId}`);
    return normalizePresentationFromApi(full.data);
  },

  getStreamUrl: (request: GeneratePresentationRequest, token: string | null): string => {
    const base = API_CONFIG.baseURL || '';
    const q = new URLSearchParams();
    if (request.method) q.set('method', request.method);
    if (request.prompt) q.set('prompt', request.prompt);
    if (request.template_id) q.set('template_id', request.template_id);
    const query = q.toString();
    return `${base}/api/v1/presentations/generate/stream?${query}`;
  },

  generateOutline: async (prompt: string, config: GenerationConfig): Promise<GenerateOutlineResponse> => {
    const { data } = await client.post<GenerateOutlineResponse>('/generate/outline', { prompt, config });
    return data;
  },

  generateOutlineFromText: async (text: string, config: GenerationConfig): Promise<GenerateOutlineResponse> => {
    const { data } = await client.post<GenerateOutlineResponse>('/generate/from-text', { text, config });
    return data;
  },

  delete: async (presentationId: string): Promise<void> => {
    await client.delete(`/${presentationId}`);
  },

  update: async (
    presentationId: string,
    payload: { title?: string; content_schema?: Presentation['content_schema'] }
  ): Promise<Presentation> => {
    const { data } = await client.patch<Presentation>(`/${presentationId}`, payload);
    return normalizePresentationFromApi(data);
  },

  /**
   * E6-8: persist full `content_schema` (includes slides) — PATCH same as `update` with body slice.
   */
  save: async (presentationId: string, contentSchema: ContentSchema): Promise<Presentation> => {
    const { data } = await client.patch<Presentation>(`/${presentationId}`, {
      content_schema: contentSchema,
    });
    return normalizePresentationFromApi(data);
  },

  /** POST /{id}/export/pdf — server ReportLab render; returns application/pdf blob (Approach A, E4-2). */
  exportPdf: async (presentationId: string): Promise<Blob> => {
    const { data } = await client.post<Blob>(`/${presentationId}/export/pdf`, null, { responseType: 'blob' });
    return data;
  },

  /** POST /{id}/export/pptx — server PptxGenJS or python-pptx; returns .pptx blob (Approach A, E4-6). */
  exportPptx: async (presentationId: string): Promise<Blob> => {
    const { data } = await client.post<Blob>(`/${presentationId}/export/pptx`, null, { responseType: 'blob' });
    return data;
  },

  /** GET /{id}/share — active share link metadata (authenticated). */
  getShareStatus: async (
    presentationId: string
  ): Promise<{ hasShare: boolean; shareToken: string | null; shareUrl: string | null }> => {
    const { data } = await client.get<{
      hasShare: boolean;
      shareToken: string | null;
      shareUrl: string | null;
    }>(`/${presentationId}/share`);
    return data;
  },

  /** POST /{id}/share — create or return opaque share token (authenticated). */
  ensureShare: async (
    presentationId: string
  ): Promise<{ shareToken: string; shareUrl: string }> => {
    const { data } = await client.post<{ shareToken: string; shareUrl: string }>(
      `/${presentationId}/share`,
      {}
    );
    return data;
  },

  /** DELETE /{id}/share — revoke public link (authenticated). */
  revokeShare: async (presentationId: string): Promise<void> => {
    await client.delete(`/${presentationId}/share`);
  },

  /**
   * GET /presentations/share/public/:token — anonymous; uses fetch (no Authorization) so public viewers work.
   */
  getByShareToken: async (shareToken: string): Promise<Presentation> => {
    const base = API_CONFIG.baseURL ?? '';
    const url = `${base}/api/v1/presentations/share/public/${encodeURIComponent(shareToken.trim())}`;
    const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
    if (!res.ok) {
      throw new Error('not_found');
    }
    const data = (await res.json()) as Presentation;
    return normalizePresentationFromApi(data);
  },

  /** GET /publish/channels — connected Slack, HubSpot, Gmail for Post flow (E4-4). */
  listPublishChannels: async (): Promise<PublishChannel[]> => {
    const { data } = await client.get<PublishChannel[]>('/publish/channels');
    return Array.isArray(data) ? data : [];
  },

  /** POST /{id}/publish — publish deck to channel (E4-4). */
  publishPresentation: async (
    presentationId: string,
    payload: { channelId: string; message?: string }
  ): Promise<PublishPresentationResponse> => {
    const { data } = await client.post<PublishPresentationResponse>(
      `/${presentationId}/publish`,
      payload
    );
    return data;
  },

  generateSpeakerNotes: async (
    presentationId: string,
    options?: { duration_minutes?: number; regenerate?: boolean }
  ): Promise<{ notes: Array<Record<string, unknown>> }> => {
    const { data } = await client.post<{ notes: Array<Record<string, unknown>> }>(
      `/${presentationId}/speaker-notes`,
      { duration_minutes: options?.duration_minutes ?? 20, regenerate: options?.regenerate ?? false }
    );
    return data;
  },

  improveText: async (
    text: string,
    improvementType: 'all' | 'clarity' | 'brevity' | 'impact' | 'grammar' | 'expand' = 'all'
  ): Promise<{ improved: string }> => {
    const { data } = await client.post<{ improved: string }>('/improve-text', {
      text,
      improvement_type: improvementType,
    });
    return data;
  },
};

function mapDealSummaryToCRMDeal(row: IntegrationsCrmDealSummary): CRMDeal {
  return {
    id: row.id,
    name: row.name,
    stage: row.stage,
    companyName: row.companyName ?? row.company_name,
    contactName: row.contactName ?? row.contact_name,
    contactTitle: row.contactTitle ?? row.contact_title,
    dealValue: row.dealValue ?? row.deal_value,
    companyIndustry: row.companyIndustry ?? row.company_industry,
    contactId: row.contactId ?? row.contact_id,
  };
}

function responseHttpStatus(e: unknown): number | undefined {
  if (axios.isAxiosError(e)) return e.response?.status;
  const st = (e as { response?: { status?: number } })?.response?.status;
  return typeof st === 'number' ? st : undefined;
}

/**
 * E5-7 — CRM helpers: same base URL resolution and auth headers as `searchIntegrationsCrmDeals` (plain axios.get).
 * 404 → empty list (no integration); 500 and other errors throw for caller handling.
 */
export const crmApi = {
  searchDeals: async (query: string): Promise<CRMDeal[]> => {
    const q = query.trim();
    if (q.length < 3) return [];
    const base = API_CONFIG.baseURL ?? '';
    const url = `${base}/api/v1/integrations/crm/deals`;
    try {
      const { data } = await axios.get<unknown>(url, {
        params: { q: q },
        headers: getAuthHeaders(),
        timeout: 15000,
      });
      return coerceDealSearchResponse(data).map(mapDealSummaryToCRMDeal);
    } catch (e) {
      if (responseHttpStatus(e) === 404) return [];
      throw e;
    }
  },

  /** GET …/contacts/:id/deck-history mapped to summaries; 404 → [] (no throw). */
  getContactDeckHistory: async (contactId: string): Promise<PreviousDeckSummary[]> => {
    try {
      const res = await presentationsApi.getContactDeckHistory(contactId);
      const decks = res.decks ?? [];
      return decks.map((d) => ({
        deckId: d.deckId,
        title: d.title,
        sentAt: d.sentAt,
        keyTopics: Array.isArray(d.keyTopics) ? d.keyTopics : [],
      }));
    } catch (e) {
      if (responseHttpStatus(e) === 404) return [];
      throw e;
    }
  },
};

/**
 * E5-3 public viewer engagement — anonymous POST, keepalive, no auth cookies.
 * Failures are swallowed (fire-and-forget).
 */
async function postViewerEngagement(
  path: '/analytics/slide-view' | '/analytics/deck-completion',
  body: unknown
): Promise<void> {
  const base = API_CONFIG.baseURL ?? '';
  const url = `${base}/api/v1/presentations${path}`;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'omit',
    });
    if (!res.ok) return;
  } catch {
    /* silent — never surface to viewer */
  }
}

export interface AnalyticsDashboardData {
  period_days: number;
  total_presentations: number;
  total_views: number;
  total_exports: number;
  most_viewed: Array<{
    id: string;
    title: string;
    view_count: number;
    unique_viewers: number;
  }>;
}

export interface ViewEventPayload {
  presentation_id: string;
  viewer_id: string;
  viewer_name?: string;
  viewed_at: string;
  duration_seconds?: number;
  device_type?: string;
}

export interface ExportEventPayload {
  presentation_id: string;
  exported_by: string;
  exported_at: string;
  format: string;
  success: boolean;
  error_message?: string;
}

export const analyticsApi = {
  getDashboard: async (days: number = 30, signal?: AbortSignal): Promise<AnalyticsDashboardData> => {
    const { data } = await client.get<AnalyticsDashboardData>('/analytics/dashboard', {
      params: { days },
      signal,
    });
    return data;
  },
  /** Authenticated GET; throws on non-2xx (same pattern as getDashboard / recordView). */
  getDeckEngagement: async (deckId: string, period: number): Promise<DeckEngagementData> => {
    const { data } = await client.get<DeckEngagementData>(
      `/analytics/decks/${encodeURIComponent(deckId.trim())}/engagement`,
      { params: { period } }
    );
    return data;
  },
  recordView: async (payload: ViewEventPayload): Promise<void> => {
    await client.post('/analytics/views', payload);
  },
  recordExport: async (payload: ExportEventPayload): Promise<void> => {
    await client.post('/analytics/exports', payload);
  },
  // fire-and-forget: anonymous POST via postViewerEngagement; network and non-2xx responses swallowed.
  trackSlideView: async (event: SlideViewEvent): Promise<void> => {
    await postViewerEngagement('/analytics/slide-view', event);
  },
  // fire-and-forget: same swallowing behaviour as trackSlideView.
  trackDeckCompletion: async (event: DeckCompletionEvent): Promise<void> => {
    await postViewerEngagement('/analytics/deck-completion', event);
  },
};

export const templatesApi = {
  list: async (params?: {
    template_type?: string;
    is_published?: boolean;
    category?: string;
    limit?: number;
    offset?: number;
  }): Promise<PresentationTemplate[]> => {
    const { data } = await client.get<PresentationTemplate[]>('/templates', {
      params: {
        template_type: params?.template_type,
        is_published: params?.is_published,
        category: params?.category,
        limit: params?.limit ?? 50,
        offset: params?.offset ?? 0,
      },
    });
    return Array.isArray(data) ? data : [];
  },

  get: async (templateId: string): Promise<PresentationTemplate> => {
    const { data } = await client.get<PresentationTemplate>(`/templates/${templateId}`);
    return data;
  },

  create: async (payload: {
    name: string;
    description?: string;
    template_type?: string;
    category: string;
    layout_schema: PresentationTemplateLayout;
    data_bindings?: Record<string, unknown>;
    branding_config?: Record<string, unknown>;
  }): Promise<PresentationTemplate> => {
    const { data } = await client.post<PresentationTemplate>('/templates', payload);
    return data;
  },

  update: async (
    templateId: string,
    payload: {
      name?: string;
      description?: string;
      layout_schema?: PresentationTemplateLayout;
      data_bindings?: Record<string, unknown>;
      is_published?: boolean;
    }
  ): Promise<PresentationTemplate> => {
    const { data } = await client.put<PresentationTemplate>(`/templates/${templateId}`, payload);
    return data;
  },
};

/** Adaptive template API response shape */
export interface AdaptiveTemplateTheme {
  id: string;
  name: string;
  description?: string;
  theme?: {
    name?: string;
    color_palette?: {
      primary?: string;
      secondary?: string;
      accent?: string;
      background?: string;
      text_primary?: string;
      text_secondary?: string;
    };
    typography?: {
      font_family_heading?: string;
      font_family_body?: string;
    };
  };
}

/** Theme preset from theme_presets.json */
export interface ThemePreset {
  id: string;
  name: string;
  category?: string;
  tags?: string[];
  colors?: Record<string, string>;
  fonts?: Record<string, string>;
  design_style?: string;
}

export interface ThemeSelectionResponse {
  selected_theme_id: string;
  theme: AdaptiveTemplateTheme;
  confidence: number;
  reasoning: string;
  alternative_themes?: AdaptiveTemplateTheme[];
}

export const adaptiveTemplatesApi = {
  list: async (params?: {
    category?: string;
    industry?: string;
    tone?: string;
    is_published?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<AdaptiveTemplateTheme[]> => {
    const { data } = await client.get<AdaptiveTemplateTheme[]>('/adaptive-templates/', {
      params: {
        category: params?.category,
        industry: params?.industry,
        tone: params?.tone,
        is_published: params?.is_published,
        limit: params?.limit ?? 50,
        offset: params?.offset ?? 0,
      },
    });
    return Array.isArray(data) ? data : [];
  },

  selectTheme: async (prompt: string, context?: Record<string, unknown>): Promise<ThemeSelectionResponse> => {
    const { data } = await client.post<ThemeSelectionResponse>('/adaptive-templates/select-theme', {
      prompt,
      context: context ?? {},
    });
    return data;
  },

  trackUsage: async (
    templateId: string,
    presentationId: string,
    modifications: Record<string, unknown> = {}
  ): Promise<void> => {
    await client.post(`/adaptive-templates/${templateId}/track-usage`, {
      presentation_id: presentationId,
      modifications,
    });
  },

  getPopularModifications: async (
    templateId: string,
    minFrequency: number = 5
  ): Promise<Array<{ modification_type: string; pattern: unknown; frequency: number }>> => {
    const { data } = await client.get(`/adaptive-templates/${templateId}/popular-modifications`, {
      params: { min_frequency: minFrequency },
    });
    return Array.isArray(data) ? data : [];
  },

  extractFromUpload: async (
    file: File,
    templateName: string
  ): Promise<AdaptiveTemplateTheme> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('template_name', templateName);
    const base = API_CONFIG.baseURL ?? '';
    const url = `${base}/api/v1/presentations/adaptive-templates/extract-upload`;
    const authHeaders = getAuthHeaders() as Record<string, string>;
    const headers: Record<string, string> = {};
    if (authHeaders.Authorization) headers.Authorization = authHeaders.Authorization;
    if (authHeaders['X-Tenant-ID']) headers['X-Tenant-ID'] = authHeaders['X-Tenant-ID'];
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as { detail?: string }).detail ?? 'Extraction failed');
    }
    return response.json();
  },

  extractFromPresentation: async (
    presentationId: string,
    templateName: string,
    description?: string
  ): Promise<AdaptiveTemplateTheme> => {
    const { data } = await client.post('/adaptive-templates/extract-from-presentation', {
      presentation_id: presentationId,
      template_name: templateName,
      description: description ?? undefined,
    });
    return data;
  },

  publish: async (templateId: string): Promise<AdaptiveTemplateTheme> => {
    const { data } = await client.patch(`/adaptive-templates/${templateId}/publish`);
    return data;
  },

  unpublish: async (templateId: string): Promise<AdaptiveTemplateTheme> => {
    const { data } = await client.patch(`/adaptive-templates/${templateId}/unpublish`);
    return data;
  },

  submitReview: async (
    templateId: string,
    rating: number,
    reviewText?: string
  ): Promise<{ status: string; rating: number }> => {
    const { data } = await client.post(`/adaptive-templates/${templateId}/reviews`, {
      rating,
      review_text: reviewText ?? undefined,
    });
    return data;
  },

  recommend: async (prompt: string): Promise<{
    recommended_templates: string[];
    recommended_themes: string[];
    default_tone: string;
    default_card_count: number;
    default_density: string;
    confidence: number;
    alternatives?: Array<Record<string, unknown>>;
  }> => {
    const { data } = await client.post(
      '/adaptive-templates/recommend',
      new URLSearchParams({ prompt }),
      { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
    );
    return data;
  },

  listReviews: async (
    templateId: string,
    limit?: number,
    offset?: number
  ): Promise<
    Array<{
      id: string;
      template_id: string;
      user_id: string | null;
      rating: number;
      review_text: string | null;
      created_at: string | null;
    }>
  > => {
    const { data } = await client.get(`/adaptive-templates/${templateId}/reviews`, {
      params: { limit, offset },
    });
    return Array.isArray(data) ? data : [];
  },
};

export const themesApi = {
  getPresets: async (): Promise<ThemePreset[]> => {
    const { data } = await client.get<ThemePreset[]>('/themes/presets');
    return Array.isArray(data) ? data : [];
  },

  createCustom: async (payload: {
    name: string;
    colors?: Record<string, string>;
    fonts?: Record<string, string>;
    design_style?: string;
    logo_url?: string;
  }): Promise<AdaptiveTemplateTheme> => {
    const { data } = await client.post<AdaptiveTemplateTheme>('/themes/custom', payload);
    return data;
  },
};

export type {
  SlideEngagement,
  DeckEngagementData,
  SlideViewEvent,
  DeckCompletionEvent,
  CRMDeal,
  PreviousDeckSummary,
} from './types';
